import React, { useState } from 'react';
import { Control, FieldError, FieldValues } from 'react-hook-form';
import { OwnerModelFormDTO, OwnerOptionsDTO, OwnerTypeDTO } from '../../../OtherAssets.meta';
import { OwnershipSelect } from './components/OwnershipSelect';
import { useAssetOwnership } from './useAssetOwnership';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Badge } from '../../../../../CommonComponents/OPL/Badge/Badge';
import { Card } from '../../../../../CommonComponents/OPL/Card/Card';
import { OwnershipPercentage } from './components/OwnershipPercentage';
import { TotalSummary } from './components/TotalSummary';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';

interface Props<T extends FieldValues> {
  control: Control<T>;
  options: OwnerOptionsDTO;
  allowsUnequalOwnership: boolean;
  ownerModelFields: OwnerModelFormDTO[];
  label?: string;
  updateOwner: (index: number, data: OwnerModelFormDTO) => void;
  appendOwner?: () => void;
  removeOwner?: (index: number) => void;
  setPercentage?: (data: number[]) => void;
  error?: string;
}

export function AssetOwnership<T extends FieldValues>({
  control,
  options,
  allowsUnequalOwnership,
  ownerModelFields,
  label = 'Owner',
  updateOwner,
  appendOwner,
  removeOwner,
  setPercentage,
  error,
}: Props<T>) {
  const { ownerOptions, allOptions } = useAssetOwnership({ options });

  const [isCustomised, setIsCustomised] = useState(ownerModelFields.length >= 2);

  const distributeEqually = () => {
    const length = ownerModelFields.length;
    const baseValue = Math.floor((100 / length) * 100) / 100; // For 2 decimal places
    const result = Array(length).fill(baseValue);
    result[length - 1] = 100 - baseValue * (length - 1);
    if (setPercentage) {
      setPercentage(result);
    }
  };

  const updateOptions = (index: number, data: { label: string; value: string | null }) => {
    const selectedOwnerOption = allOptions.find(option =>
      option.Id !== null ? option.Id === data.value : option.Name === data.label,
    );
    if (selectedOwnerOption) {
      updateOwner(index, {
        Id: data.value,
        Name: data.label,
        OwnerType: selectedOwnerOption.OwnerType,
      });
    }
  };

  const handleCustomised = () => {
    setIsCustomised(true);
    const peopleOptions = ownerOptions[0].options;
    if (ownerModelFields[0].Name === 'Joint') {
      updateOwner(0, {
        Id: peopleOptions[0].value,
        Name: peopleOptions[0].label,
        OwnerType: OwnerTypeDTO.Principal,
        Percentage: '100',
      });
    }
  };

  const selectProps = {
    control,
    allowsUnequalOwnership,
    groupOptions: ownerOptions,
    ownerModelFields,
    label,
  };

  return (
    <>
      {error && <Alert mt="16" message={error} variant="error" />}
      {ownerModelFields.length >= 2 || isCustomised ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" css="gap: 8px">
              {ownerModelFields.length === 1 && (
                <IconButton
                  icon="fas fa-arrow-left"
                  variant="subtleInactive"
                  onClick={() => {
                    updateOwner(0, {
                      Percentage: '100',
                    });
                    setIsCustomised(false);
                  }}
                />
              )}
              <Text fontSize="body" fontWeight={600} color="neutral.600">
                Asset Ownership
              </Text>
            </Box>

            <Box display="flex" css="gap: 8px">
              <Button
                variant="secondary"
                disabled={ownerModelFields.length === 0}
                onClick={distributeEqually}
              >
                Distribute Equally
              </Button>
              <Button
                variant="secondary"
                leftIcon="fas fa-plus"
                onClick={appendOwner}
                disabled={ownerModelFields.length === allOptions.length}
              >
                Add Owner
              </Button>
            </Box>
          </Box>
          {ownerModelFields.map(({ Name, OwnerType }, index) => (
            <Card key={index} mt="4" p="16">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="baseline" css="gap:8px">
                  <Text fontSize="body" mb="8">
                    {Name}
                  </Text>
                  {Name && OwnerType !== undefined && (
                    <Badge size="small" variant="tertiary">
                      {OwnerTypeDTO[OwnerType]}
                    </Badge>
                  )}
                </Box>
                {index !== 0 && (
                  <IconButton
                    icon="far fa-trash-alt"
                    variant="subtleActive"
                    color="error.500"
                    onClick={() => removeOwner && removeOwner(index)}
                  />
                )}
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
                css="gap: 16px"
                mt="4"
              >
                <OwnershipSelect<T>
                  {...selectProps}
                  index={index}
                  onUpdate={data => updateOptions(index, data)}
                />
                <OwnershipPercentage<T> index={index} control={control} />
              </Box>
            </Card>
          ))}
          <TotalSummary ownerModelFields={ownerModelFields} />
        </>
      ) : (
        <OwnershipSelect<T>
          {...selectProps}
          index={0}
          onUpdate={data => updateOptions(0, data)}
          setCustomised={handleCustomised}
        />
      )}
    </>
  );
}
